'use client';

import * as Styled from './layout.styled';
import ProductsNav from '../_components/Products/ProductsNav';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { OrganizationSelector } from '@modules/organizations/pages/organizations-list';
import { NavActions } from 'app/(authenticated)/_components/NavActions/NavActions';
import { WorkspaceSelector } from 'app/(authenticated)/_components/WorkspaceSelector';
import CriblIcon from 'app/_assets/icons/CriblIcon.svg';
import ProductsIcon from 'app/_components/Icons/ProductsIcon';
import { useOrganization } from 'app/_providers/Organization';
import { useUser } from 'app/_providers/User';
import { useWorkspace } from 'app/_providers/WorkspaceProvider';
import useOrganizationCommand from 'app/_utils/data/organization';

import { Divider, Toast } from '@capra/core';
import React, { useState, useRef } from 'react';
import { AccountState } from 'shared/src/account/types';
import { isUserOrgOwner } from 'shared/src/authz/validators';

const OrganizationsLayout = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const { cloudEphemeralSandboxesOrganizations } = useFlagsProxy();
  const user = useUser();
  const { isOEM } = user;
  const isOwner = isUserOrgOwner(user);
  const { organizationId, license } = useOrganization();
  const { requestLicenseUpgrade } = useOrganizationCommand();
  const isEnterprise = license === 'enterprise';
  const { state } = useWorkspace();
  const [showProducts, setShowProducts] = useState(false);
  const [initials, setInitials] = useState('');
  const productsRef = useRef<HTMLDivElement>(null);
  const isProd = process.env.ENVIRONMENT === 'production';
  const shouldEnableGoEnterpriseButton = !isEnterprise && !isOEM && isOwner;

  const workspaceActive = state === AccountState.WorkspaceActive;

  React.useEffect(() => {
    if (user?.name) {
      const fullName = user.name.split(' ');
      setInitials((fullName[0]?.[0] || '') + (fullName[1]?.[0] || ''));
    }
  }, [user]);

  React.useEffect(() => {
    // make sure clicking off closes the drawer
    const handleClickOff = (e: MouseEvent) => {
      if (!productsRef.current?.contains(e.target as HTMLElement)) {
        setShowProducts(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProducts(false);
      }
    };
    document.addEventListener('click', handleClickOff);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('click', handleClickOff);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowProducts]);

  const sendLicenseUpgradeRequest = (): void => {
    requestLicenseUpgrade(user.sub);
    Toast.success('Cribl Sales will get in touch with you. Thank you for your interest in Cribl!', { position: 'bottom-right' });
  };

  return (
    <Styled.Wrapper>
      <Styled.Header>
        <Styled.ContextWrapper>
          <Styled.LogoLink
            href={{ pathname: `/organizations/${organizationId}` }}
          >
            <CriblIcon />
          </Styled.LogoLink>

          <Divider type='vertical' />

          {!isOEM && workspaceActive && (
            <Styled.ProductSelector ref={productsRef} data-testid='top-nav-products-selector'>
              <Styled.ProductDropdownWraper onClick={() => setShowProducts((prevState) => !prevState)}>
                <ProductsIcon />

                <span>Products</span>
              </Styled.ProductDropdownWraper>
            </Styled.ProductSelector>
          )}

          {shouldEnableGoEnterpriseButton && (
            <Styled.GoEnterpriseButton
              type='primary'
              onClick={sendLicenseUpgradeRequest}
            >
              Go Enterprise
            </Styled.GoEnterpriseButton>
          )}

          <OrganizationSelector data-testid='organization-selector' isOEM={isOEM} />

          <WorkspaceSelector isOEM={isOEM} />

        </Styled.ContextWrapper>

        {!isProd && <Styled.NonProductionTag>Non-Production Environment</Styled.NonProductionTag>}

        {Array.isArray(cloudEphemeralSandboxesOrganizations) &&
          cloudEphemeralSandboxesOrganizations.includes(organizationId) && (
            <Styled.NonProductionTag>Sandbox Environment</Styled.NonProductionTag>
        )}

        <NavActions userInitials={initials} />
      </Styled.Header>

      {showProducts && <ProductsNav />}

      {children}
    </Styled.Wrapper>
  );
};

export default OrganizationsLayout;
